// Shared micro-components & helpers for admin pages
import React, { useEffect, useState, useCallback } from "react";
import AdminPortal from "../../components/admin/AdminPortal";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Info,
  Inbox,
  Loader2,
  Pencil,
  Search,
  Trash2,
  X,
} from "lucide-react";

export const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8081";
export const API = `${API_BASE}/api`;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const isRetryableStatus = (status) =>
  [408, 425, 429, 500, 502, 503, 504, 520, 522, 524].includes(Number(status));

// ─── Thông báo lỗi dễ hiểu ───────────────────────────────────────────────────
// Lỗi thô ("Failed to fetch", "HTTP 502", HTML của gateway) → câu người dùng hiểu được
// + gợi ý việc nên làm. Chi tiết kỹ thuật để riêng trong err.detail (hiện nhỏ trong toast).
const STATUS_MESSAGE = {
  400: "Dữ liệu gửi lên không hợp lệ. Vui lòng kiểm tra lại các ô đã nhập.",
  401: "Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại rồi thử lại.",
  403: "Tài khoản của bạn không có quyền thực hiện thao tác này.",
  404: "Không tìm thấy dữ liệu — có thể mục này đã bị xoá. Hãy tải lại trang.",
  408: "Máy chủ phản hồi quá lâu. Vui lòng thử lại.",
  409: "Dữ liệu vừa được thay đổi ở nơi khác hoặc đang được xử lý. Hãy tải lại trang rồi thử lại.",
  413: "Tệp hoặc nội dung gửi lên quá lớn. Hãy giảm dung lượng ảnh/video rồi thử lại.",
  422: "Một số thông tin chưa hợp lệ. Vui lòng kiểm tra lại các ô được đánh dấu.",
  429: "Bạn thao tác quá nhanh. Vui lòng đợi một lát rồi thử lại.",
};

const pathOf = (url) => {
  try {
    return new URL(url).pathname;
  } catch {
    return String(url || "");
  }
};

/** Câu thông báo cho lỗi HTTP: ưu tiên message tiếng Việt BE trả về, nếu không thì theo mã lỗi. */
export function friendlyHttpMessage(status, serverMessage) {
  const s = Number(status);
  const fromServer = String(serverMessage || "").trim();
  // BE trả { message } có nghĩa → dùng luôn; bỏ qua HTML/chuỗi gateway chung chung
  const generic = /^(internal server error|bad gateway|service unavailable|gateway time-?out|http \d+)$/i;
  if (fromServer && !/^</.test(fromServer) && !generic.test(fromServer) && fromServer.length <= 300) {
    return fromServer;
  }
  if (STATUS_MESSAGE[s]) return STATUS_MESSAGE[s];
  if (s >= 500) return `Máy chủ đang gặp sự cố (mã ${s}). Dữ liệu bạn nhập vẫn được giữ — vui lòng thử lại sau ít phút.`;
  return `Không thực hiện được yêu cầu (mã ${s}). Vui lòng thử lại.`;
}

/** Tiêu đề toast theo loại lỗi. */
export function errorTitle(err) {
  const s = Number(err?.status);
  if (!s) return "Không kết nối được máy chủ";
  if (s === 401) return "Hết phiên đăng nhập";
  if (s === 403) return "Không có quyền";
  if (s === 413) return "Tệp quá lớn";
  if (s === 422 || s === 400) return "Thông tin chưa hợp lệ";
  if (s >= 500) return "Máy chủ gặp sự cố";
  return "Có lỗi";
}

/** Câu thông báo khi không gọi được máy chủ (fetch ném TypeError: mạng, CORS, máy chủ sập). */
export function friendlyNetworkMessage() {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return "Mất kết nối Internet. Kiểm tra mạng rồi thử lại — dữ liệu bạn nhập vẫn được giữ.";
  }
  return "Không kết nối được máy chủ. Máy chủ có thể đang gặp sự cố hoặc mạng bị gián đoạn — dữ liệu bạn nhập vẫn được giữ, vui lòng thử lại sau ít phút. Nếu vẫn lỗi, hãy báo kỹ thuật.";
}

function withStatus(err, status) {
  if (!err || typeof err !== "object") return err;
  try {
    err.status = status;
  } catch {
    // ignore
  }
  return err;
}

function jitter(ms) {
  const n = Number(ms) || 0;
  if (n <= 0) return 0;
  // +/- 20%
  const spread = n * 0.2;
  return Math.max(0, Math.round(n - spread + Math.random() * spread * 2));
}

/**
 * Bearer cho API admin.
 * Luôn ưu tiên authToken (đăng nhập trên site) trước adminToken — tránh token adminToken cũ/hết hạn
 * vẫn nằm trong localStorage làm API dùng nhầm và trả 401/403 dù đã đăng nhập admin bằng authToken.
 */
export const ADMIN_SESSION_EXPIRED_EVENT = "admin:session-expired";

export const getAdminSessionToken = () =>
  localStorage.getItem("authToken") || localStorage.getItem("adminToken") || "";

// ─── Admin color tokens (khớp CSS variables trong index.css) ──────────────────
export const C = {
  primary: "#256abf",
  primaryHover: "#1c5cab",
  danger: "#dc2626",
  focusRing: "rgba(37, 106, 191, 0.18)",
};

export const apiFetch = async (url, options = {}) => {
  const token = getAdminSessionToken();
  const baseHeaders = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const method = String(options?.method || "GET").toUpperCase();
  const retryCfg = options?.retry || null;
  const onRetry = typeof retryCfg?.onRetry === "function" ? retryCfg.onRetry : null;
  const allowNonIdempotent = Boolean(retryCfg?.allowNonIdempotent);
  const defaultRetries = method === "GET" || method === "HEAD" ? 2 : 0;
  const retries = Math.max(0, Number(retryCfg?.retries ?? defaultRetries) || 0);
  const baseDelayMs = Math.max(0, Number(retryCfg?.baseDelayMs ?? 450) || 0);
  const maxDelayMs = Math.max(0, Number(retryCfg?.maxDelayMs ?? 4000) || 0);
  const shouldRetryMethod =
    method === "GET" || method === "HEAD" || method === "PUT" || method === "PATCH" || allowNonIdempotent;

  // Only set JSON content-type when caller didn't provide one.
  // This keeps FormData requests working and avoids clobbering custom headers.
  if (!options?.headers || !("Content-Type" in options.headers)) {
    baseHeaders["Content-Type"] = "application/json";
  }

  let lastErr = null;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const { retry, ...fetchOptions } = options || {};
      const res = await fetch(url, {
        ...fetchOptions,
        headers: {
          ...baseHeaders,
          ...(fetchOptions.headers || {}),
        },
      });

      const raw = await res.text();
      let data = null;
      try {
        data = raw ? JSON.parse(raw) : null;
      } catch {
        data = null;
      }

      if (!res.ok) {
        const serverMsg =
          data?.message ||
          data?.error ||
          (typeof data === "string" ? data : "") ||
          raw;
        // 401 khi đang gửi token = phiên hết hạn/không hợp lệ → AdminLayout đăng xuất & chuyển tới trang đăng nhập
        if (res.status === 401 && token) {
          window.dispatchEvent(new CustomEvent(ADMIN_SESSION_EXPIRED_EVENT));
        }
        const err = withStatus(
          new Error(res.status === 401 ? STATUS_MESSAGE[401] : friendlyHttpMessage(res.status, serverMsg)),
          res.status
        );
        err.detail = `${method} ${pathOf(url)} → HTTP ${res.status}${data?.code ? ` (${data.code})` : ""}`;
        // Chuẩn lỗi BE: { message, code, details? } — để form hiển thị lỗi theo từng field.
        err.code = data?.code;
        err.details = data?.details;

        const canRetry =
          attempt < retries && shouldRetryMethod && isRetryableStatus(res.status);
        if (canRetry) {
          lastErr = err;
          const wait = Math.min(maxDelayMs, baseDelayMs * Math.pow(2, attempt));
          try {
            onRetry?.({ attempt: attempt + 1, retries, waitMs: wait, err, url, method });
          } catch {
            // ignore
          }
          await sleep(jitter(wait));
          continue;
        }
        throw err;
      }

      return data ?? {};
    } catch (err) {
      // Network error / aborted / DNS / CORS etc. fetch() throws TypeError.
      const isNetwork = err instanceof TypeError || /network|fetch|failed/i.test(String(err?.message || ""));
      const canRetry =
        attempt < retries && shouldRetryMethod && (isNetwork || isRetryableStatus(err?.status));
      if (canRetry) {
        lastErr = err;
        const wait = Math.min(maxDelayMs, baseDelayMs * Math.pow(2, attempt));
        try {
          onRetry?.({ attempt: attempt + 1, retries, waitMs: wait, err, url, method, isNetwork });
        } catch {
          // ignore
        }
        await sleep(jitter(wait));
        continue;
      }
      if (isNetwork && err?.status == null) {
        // "Failed to fetch" không nói gì — câu dễ hiểu cho người dùng, request cụ thể để tra (CORS / Lambda sập / quá 6MB).
        const wrapped = new Error(friendlyNetworkMessage());
        wrapped.cause = err;
        wrapped.uncertain = true;
        wrapped.detail = `${method} ${pathOf(url)}: ${err?.message || "network error"}`;
        throw wrapped;
      }
      throw err;
    }
  }

  throw lastErr || new Error("Request failed");
};

// ─── Class helper ────────────────────────────────────────────────────────────
export const cx = (...parts) => parts.filter(Boolean).join(" ");

// ─── Button ──────────────────────────────────────────────────────────────────
const BTN_VARIANT = {
  primary:
    "bg-admin-primary text-white shadow-sm hover:bg-admin-primaryHover disabled:hover:bg-admin-primary",
  secondary:
    "border border-admin-border bg-admin-surface text-admin-text shadow-sm hover:bg-admin-muted",
  ghost: "text-admin-textMuted hover:bg-admin-muted hover:text-admin-text",
  danger: "bg-admin-danger text-white shadow-sm hover:bg-admin-danger/90",
  soft: "bg-admin-primaryLight text-admin-primary hover:bg-admin-primary/15",
};
const BTN_SIZE = {
  sm: "h-8 gap-1.5 rounded-lg px-3 text-xs",
  md: "h-10 gap-2 rounded-lg px-4 text-sm",
  lg: "h-11 gap-2 rounded-xl px-5 text-sm",
  icon: "h-9 w-9 rounded-lg",
};

export const Button = React.forwardRef(function Button(
  { variant = "secondary", size = "md", icon: Icon, loading = false, disabled, className, children, type = "button", ...rest },
  ref
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={cx(
        "inline-flex shrink-0 items-center justify-center font-semibold transition active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50",
        BTN_VARIANT[variant] || BTN_VARIANT.secondary,
        BTN_SIZE[size] || BTN_SIZE.md,
        className
      )}
      {...rest}
    >
      {loading ? <Loader2 size={15} className="animate-spin" /> : Icon ? <Icon size={size === "sm" ? 14 : 16} /> : null}
      {children}
    </button>
  );
});

// ─── Card ────────────────────────────────────────────────────────────────────
export const Card = ({ className, children, as: Tag = "section", ...rest }) => (
  <Tag
    className={cx("rounded-2xl border border-admin-border bg-admin-surface shadow-[0_1px_2px_rgba(15,23,42,0.05),0_4px_16px_-6px_rgba(15,23,42,0.08)]", className)}
    {...rest}
  >
    {children}
  </Tag>
);

export const CardHeader = ({ title, subtitle, action, className }) => (
  <div className={cx("flex items-start justify-between gap-3 px-5 pb-0 pt-5", className)}>
    <div className="min-w-0">
      <h2 className="truncate text-[15px] font-semibold text-admin-text">{title}</h2>
      {subtitle ? <p className="mt-0.5 text-xs text-admin-textMuted">{subtitle}</p> : null}
    </div>
    {action ? <div className="shrink-0">{action}</div> : null}
  </div>
);

// ─── Badge ───────────────────────────────────────────────────────────────────
const BADGE_TONE = {
  neutral: "bg-admin-muted text-admin-textMuted",
  success: "bg-emerald-50 text-emerald-700",
  danger: "bg-rose-50 text-rose-700",
  warning: "bg-amber-50 text-amber-700",
  primary: "bg-admin-primaryLight text-admin-primary",
  info: "bg-sky-50 text-sky-700",
};
export const Badge = ({ tone = "neutral", dot = false, className, children }) => (
  <span
    className={cx(
      "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold leading-5",
      BADGE_TONE[tone] || BADGE_TONE.neutral,
      className
    )}
  >
    {dot ? <span className="h-1.5 w-1.5 rounded-full bg-current opacity-80" /> : null}
    {children}
  </span>
);

// ─── StatCard ────────────────────────────────────────────────────────────────
const STAT_TONE = {
  primary: "bg-admin-primaryLight text-admin-primary",
  amber: "bg-amber-50 text-amber-700",
  sky: "bg-sky-50 text-sky-700",
  rose: "bg-rose-50 text-rose-700",
  emerald: "bg-emerald-50 text-emerald-700",
  slate: "bg-admin-muted text-admin-textMuted",
};
export const StatCard = ({ label, value, hint, icon: Icon, tone = "primary", to, loading = false }) => {
  const inner = (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="truncate text-xs font-medium text-admin-textMuted">{label}</p>
        {loading ? (
          <div className="mt-2 h-8 w-20 animate-pulse rounded-md bg-admin-muted" />
        ) : (
          <p className="mt-1.5 text-[28px] font-semibold leading-none tracking-tight tabular-nums text-admin-text">
            {value ?? "—"}
          </p>
        )}
        {hint ? <p className="mt-2 line-clamp-1 text-xs text-admin-textMuted">{hint}</p> : null}
      </div>
      {Icon ? (
        <span className={cx("flex h-10 w-10 shrink-0 items-center justify-center rounded-lg", STAT_TONE[tone] || STAT_TONE.primary)}>
          <Icon size={18} strokeWidth={2} />
        </span>
      ) : null}
    </div>
  );
  const cls =
    "block rounded-xl border border-admin-border bg-admin-surface p-4 shadow-soft transition";
  return to ? (
    <Link to={to} className={cx(cls, "hover:border-admin-primary/40 hover:shadow-soft-md")}>
      {inner}
    </Link>
  ) : (
    <div className={cls}>{inner}</div>
  );
};

// ─── SegmentedControl ────────────────────────────────────────────────────────
export const SegmentedControl = ({ value, onChange, options, className, ariaLabel }) => (
  <div
    role="tablist"
    aria-label={ariaLabel}
    className={cx("inline-flex items-center gap-0.5 rounded-lg bg-admin-muted p-0.5", className)}
  >
    {options.map((o) => {
      const on = o.value === value;
      return (
        <button
          key={o.value}
          type="button"
          role="tab"
          aria-selected={on}
          onClick={() => onChange(o.value)}
          className={cx(
            "rounded-md px-3 py-1.5 text-xs font-semibold transition",
            on ? "bg-admin-surface text-admin-text shadow-sm" : "text-admin-textMuted hover:text-admin-text"
          )}
        >
          {o.label}
        </button>
      );
    })}
  </div>
);

// ─── Pagination ──────────────────────────────────────────────────────────────
export const Pagination = ({ page = 1, pages = 1, total, onChange, className }) => {
  if (pages <= 1) {
    return total != null ? (
      <p className={cx("px-5 py-3 text-xs text-admin-textMuted", className)}>{total} mục</p>
    ) : null;
  }
  const items = [];
  const push = (v) => items.push(v);
  const around = new Set([1, pages, page - 1, page, page + 1]);
  let last = 0;
  for (let p = 1; p <= pages; p++) {
    if (!around.has(p)) continue;
    if (p - last > 1) push("…" + p);
    push(p);
    last = p;
  }
  return (
    <div className={cx("flex flex-wrap items-center justify-between gap-3 px-5 py-3", className)}>
      <p className="text-xs text-admin-textMuted">
        Trang <span className="font-semibold text-admin-text">{page}</span> / {pages}
        {total != null ? ` · ${total} mục` : ""}
      </p>
      <div className="flex items-center gap-1">
        <Button size="icon" variant="ghost" aria-label="Trang trước" disabled={page <= 1} onClick={() => onChange(page - 1)}>
          <ChevronLeft size={16} />
        </Button>
        {items.map((it) =>
          typeof it === "string" ? (
            <span key={it} className="px-1 text-xs text-admin-textMuted">…</span>
          ) : (
            <button
              key={it}
              type="button"
              onClick={() => onChange(it)}
              aria-current={it === page ? "page" : undefined}
              className={cx(
                "h-8 min-w-[2rem] rounded-lg px-2 text-xs font-semibold tabular-nums transition",
                it === page ? "bg-admin-primary text-white" : "text-admin-textMuted hover:bg-admin-muted hover:text-admin-text"
              )}
            >
              {it}
            </button>
          )
        )}
        <Button size="icon" variant="ghost" aria-label="Trang sau" disabled={page >= pages} onClick={() => onChange(page + 1)}>
          <ChevronRight size={16} />
        </Button>
      </div>
    </div>
  );
};

// ─── Toast ───────────────────────────────────────────────────────────────────
const TOAST_VARIANT = {
  success: { Icon: CheckCircle2, title: "Thành công", tone: "text-emerald-600", bar: "bg-emerald-500" },
  error: { Icon: AlertCircle, title: "Có lỗi", tone: "text-rose-600", bar: "bg-rose-500" },
  info: { Icon: Info, title: "Thông báo", tone: "text-sky-600", bar: "bg-sky-500" },
};

const DURATION = 4200;

const SingleToast = ({ msg, type = "success", onClose, action, duration = DURATION, title, detail }) => {
  const [visible, setVisible] = useState(false);
  const [paused, setPaused] = useState(false);
  const v = TOAST_VARIANT[type] || TOAST_VARIANT.success;
  const { Icon } = v;

  useEffect(() => {
    const show = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(show);
  }, []);

  // Dừng đếm ngược khi rê chuột vào để người dùng kịp đọc.
  useEffect(() => {
    if (paused) return undefined;
    const close = setTimeout(() => {
      setVisible(false);
      setTimeout(onClose, 220);
    }, duration);
    return () => clearTimeout(close);
  }, [paused, onClose, duration]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 220);
  };

  return (
    <div
      role={type === "error" ? "alert" : "status"}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className={cx(
        "pointer-events-auto relative flex w-[min(calc(100vw-2rem),380px)] items-start gap-3 overflow-hidden rounded-xl border border-admin-border bg-admin-surface py-3 pl-4 pr-2 shadow-soft-lg transition duration-200",
        visible ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0"
      )}
    >
      <span className={cx("absolute inset-y-0 left-0 w-1", v.bar)} aria-hidden />
      <Icon size={18} className={cx("mt-0.5 shrink-0", v.tone)} />
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-semibold text-admin-text">{title || v.title}</p>
        <p className="mt-0.5 break-words text-[13px] leading-snug text-admin-textMuted">{msg}</p>
        {detail ? (
          <p className="mt-1 break-all font-mono text-[11px] leading-snug text-admin-textMuted/80" title="Chi tiết kỹ thuật — gửi kèm khi báo lỗi">
            {detail}
          </p>
        ) : null}
        {action ? (
          <button
            type="button"
            onClick={() => {
              action.onClick?.();
              handleClose();
            }}
            className="mt-1.5 text-[13px] font-semibold text-admin-primary hover:underline"
          >
            {action.label}
          </button>
        ) : null}
      </div>
      <button
        type="button"
        onClick={handleClose}
        aria-label="Đóng"
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-admin-textMuted transition hover:bg-admin-muted hover:text-admin-text"
      >
        <X size={15} />
      </button>
    </div>
  );
};

// Toast container — bottom-right stack
export const ToastContainer = ({ toasts, onClose }) => (
  <div
    className="pointer-events-none fixed bottom-4 right-4 z-[9999] flex flex-col items-end gap-2 sm:bottom-6 sm:right-6"
    aria-live="polite"
  >
    {toasts.map((t) => (
      <SingleToast key={t.id} {...t} onClose={() => onClose(t.id)} />
    ))}
  </div>
);

// Legacy single-toast for pages using old API
export const Toast = ({ msg, type, onClose }) => {
  const [toasts] = useState([{ id: 1, msg, type }]);
  return <ToastContainer toasts={toasts} onClose={onClose} />;
};

// ─── useToast hook ────────────────────────────────────────────────────────────
// push/remove must be stable (useCallback) so callers can safely put them in
// useCallback/useEffect deps — otherwise every render retriggers fetch loops.
export const useToast = () => {
  const [toasts, setToasts] = useState([]);
  const lastRef = React.useRef({ key: "", at: 0 });
  const MAX_TOASTS = 4;
  const DEDUPE_MS = 1600;
  const push = useCallback((msg, type = "success", opts = {}) => {
    // Cho phép truyền thẳng Error: lấy message dễ hiểu + chi tiết kỹ thuật (err.detail) từ apiFetch
    const errObj = msg instanceof Error ? msg : opts?.error instanceof Error ? opts.error : null;
    const text = String((msg instanceof Error ? msg.message : msg) || "").trim();
    const t = String(type || "success");
    const detail = opts?.detail || errObj?.detail || "";
    const title = opts?.title || (t === "error" && errObj ? errorTitle(errObj) : "");
    const key = `${t}:${text}`;
    const now = Date.now();

    // Avoid spamming identical messages during cold start / flaky network.
    if (lastRef.current.key === key && now - lastRef.current.at < DEDUPE_MS) return;
    lastRef.current = { key, at: now };

    const id = now + Math.random();
    setToasts((prev) => {
      // Lỗi cần đọc kỹ → hiện lâu hơn
      const minDuration = opts?.action ? 8000 : t === "error" ? 7000 : 0;
      const duration = Math.max(opts?.duration || DURATION, minDuration);
      const next = [...prev, { id, msg: text || "Có lỗi", type: t, action: opts?.action, duration, title, detail }];
      return next.length > MAX_TOASTS ? next.slice(next.length - MAX_TOASTS) : next;
    });
  }, []);
  const remove = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);
  const ToastPortal = useCallback(
    () => <ToastContainer toasts={toasts} onClose={remove} />,
    [toasts, remove]
  );
  return { push, ToastPortal };
};

// ─── Overlay helpers ─────────────────────────────────────────────────────────
function useEscape(open, onClose) {
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);
}

// ─── ConfirmModal ─────────────────────────────────────────────────────────────
export const ConfirmModal = ({
  message,
  description,
  onConfirm,
  onCancel,
  confirmLabel = "Xoá",
  cancelLabel = "Huỷ bỏ",
  tone = "danger",
  loading = false,
}) => {
  useEscape(true, onCancel);
  const danger = tone === "danger";
  return (
    <AdminPortal>
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
        <button
          type="button"
          aria-label="Đóng"
          className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]"
          style={{ animation: "admin-fade-in 0.15s ease-out" }}
          onClick={onCancel}
        />
        <div
          className="relative w-full max-w-[400px] overflow-hidden rounded-2xl border border-admin-border bg-admin-surface shadow-soft-xl"
          style={{ animation: "admin-pop-in 0.18s ease-out" }}
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="confirm-title"
        >
          <div className="flex gap-4 p-5">
            <span
              className={cx(
                "flex h-10 w-10 shrink-0 items-center justify-center rounded-full",
                danger ? "bg-rose-50 text-rose-600" : "bg-admin-primaryLight text-admin-primary"
              )}
            >
              {danger ? <Trash2 size={18} /> : <Info size={18} />}
            </span>
            <div className="min-w-0">
              <p id="confirm-title" className="text-[15px] font-semibold text-admin-text">{message}</p>
              {description ? (
                <p className="mt-1.5 text-sm leading-relaxed text-admin-textMuted">{description}</p>
              ) : null}
            </div>
          </div>
          <div className="flex justify-end gap-2 border-t border-admin-border bg-admin-muted/50 px-5 py-3">
            <Button variant="secondary" onClick={onCancel} disabled={loading}>
              {cancelLabel}
            </Button>
            <Button variant={danger ? "danger" : "primary"} onClick={onConfirm} loading={loading} autoFocus>
              {confirmLabel}
            </Button>
          </div>
        </div>
      </div>
    </AdminPortal>
  );
};

// ─── Modal (centered dialog) ───────────────────────────────────────────────────
export const Modal = ({
  open,
  onClose,
  title,
  children,
  footer,
  headerRight,
  maxWidthClass = "max-w-lg",
  panelClassName = "",
  zClass = "z-[150]", // dialog mở chồng lên modal khác cần z cao hơn
}) => {
  useEscape(open, onClose);
  if (!open) return null;
  return (
    <AdminPortal>
      <div
        className={cx("fixed inset-0 flex items-center justify-center p-4 sm:p-6", zClass)}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        <button
          type="button"
          aria-label="Đóng"
          className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]"
          style={{ animation: "admin-fade-in 0.15s ease-out" }}
          onClick={onClose}
        />
        <div
          className={cx(
            "relative flex max-h-[min(90vh,760px)] w-full flex-col overflow-hidden rounded-2xl border border-admin-border bg-admin-surface shadow-soft-xl",
            maxWidthClass,
            panelClassName
          )}
          style={{ animation: "admin-pop-in 0.18s ease-out" }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-admin-border px-5 py-3.5 sm:px-6">
            {typeof title === "string" ? (
              <h2 id="modal-title" className="text-base font-semibold tracking-tight text-admin-text">
                {title}
              </h2>
            ) : (
              <div id="modal-title" className="min-w-0 flex-1">
                {title}
              </div>
            )}
            <div className="flex items-center gap-2">
              {headerRight ? <div>{headerRight}</div> : null}
              <button
                type="button"
                onClick={onClose}
                aria-label="Đóng"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-admin-textMuted transition hover:bg-admin-muted hover:text-admin-text"
              >
                <X size={18} />
              </button>
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6">{children}</div>
          {footer && (
            <div className="shrink-0 border-t border-admin-border bg-admin-muted/40 px-5 py-3.5 sm:px-6">{footer}</div>
          )}
        </div>
      </div>
    </AdminPortal>
  );
};

// ─── Drawer (right-side panel) ────────────────────────────────────────────────
export const Drawer = ({ open, onClose, title, children, footer, width = "max-w-xl" }) => {
  useEscape(open, onClose);
  if (!open) return null;
  return (
    <AdminPortal>
      <div className="fixed inset-0 z-[100] flex justify-end" role="dialog" aria-modal="true">
        <button
          type="button"
          aria-label="Đóng"
          className="absolute inset-0 bg-slate-900/30 backdrop-blur-[2px]"
          style={{ animation: "admin-fade-in 0.15s ease-out" }}
          onClick={onClose}
        />
        <div
          className={cx("relative flex h-full w-full flex-col border-l border-admin-border bg-admin-surface shadow-soft-xl", width)}
          style={{ animation: "admin-slide-in-right 0.2s ease-out" }}
        >
          <div className="flex h-14 shrink-0 items-center justify-between border-b border-admin-border px-5">
            <h3 className="text-[15px] font-semibold text-admin-text">{title}</h3>
            <button
              type="button"
              onClick={onClose}
              aria-label="Đóng"
              className="flex h-8 w-8 items-center justify-center rounded-lg text-admin-textMuted transition hover:bg-admin-muted hover:text-admin-text"
            >
              <X size={18} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-5">{children}</div>
          {footer && <div className="shrink-0 border-t border-admin-border bg-admin-muted/40 px-5 py-3.5">{footer}</div>}
        </div>
      </div>
    </AdminPortal>
  );
};

// ─── Field ────────────────────────────────────────────────────────────────────
export const Field = ({ label, required, hint, error, name, children }) => (
  <div data-field={name} data-invalid={error ? "true" : undefined}>
    {label ? (
      <label className="mb-1.5 flex items-center gap-1 text-xs font-semibold text-admin-text">
        {label}
        {required && <span className="text-rose-600">*</span>}
      </label>
    ) : null}
    {children}
    {error ? (
      <p className="mt-1.5 text-xs font-medium text-rose-600">{error}</p>
    ) : hint ? (
      <p className="mt-1.5 text-xs leading-relaxed text-admin-textMuted">{hint}</p>
    ) : null}
  </div>
);

// ─── Input ────────────────────────────────────────────────────────────────────
export const iCls =
  "w-full rounded-lg border border-admin-border bg-admin-surface px-3 py-2 text-sm text-admin-text outline-none transition placeholder:text-admin-textMuted/60 hover:border-admin-textMuted/40 focus:border-admin-primary focus:ring-4 focus:ring-admin-primary/10 disabled:cursor-not-allowed disabled:bg-admin-muted disabled:text-admin-textMuted";

// ─── Toggle ───────────────────────────────────────────────────────────────────
export const Toggle = ({ checked, onChange, labelOn = "Hiển thị", labelOff = "Ẩn" }) => (
  <label className="inline-flex cursor-pointer items-center gap-3">
    <span className="relative">
      <input type="checkbox" role="switch" className="peer sr-only" checked={checked} onChange={onChange} />
      <span className="block h-6 w-11 rounded-full bg-admin-border transition-colors duration-200 peer-checked:bg-admin-primary peer-focus-visible:ring-4 peer-focus-visible:ring-admin-primary/20" />
      <span className="pointer-events-none absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform duration-200 peer-checked:translate-x-5" />
    </span>
    <span className={cx("text-sm font-medium", checked ? "text-admin-text" : "text-admin-textMuted")}>
      {checked ? labelOn : labelOff}
    </span>
  </label>
);

// ─── SaveButton ───────────────────────────────────────────────────────────────
export const SaveButton = ({ saving, labelAdd, labelEdit, isEdit, form }) => (
  <Button type="submit" variant="primary" size="lg" loading={saving} className="flex-1" {...(form ? { form } : {})}>
    {isEdit ? labelEdit || "Lưu thay đổi" : labelAdd || "Thêm mới"}
  </Button>
);

// ─── PageHeader ───────────────────────────────────────────────────────────────
export const PageHeader = ({ title, subtitle, action }) => (
  <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
    <div className="min-w-0">
      <h1 className="text-2xl font-semibold tracking-tight text-admin-text">{title}</h1>
      {subtitle && <p className="mt-1 text-sm text-admin-textMuted">{subtitle}</p>}
    </div>
    {action && <div className="flex flex-wrap items-center gap-2">{action}</div>}
  </div>
);

// ─── TableCard ────────────────────────────────────────────────────────────────
export const TableCard = ({ children }) => (
  <div className="overflow-hidden rounded-xl border border-admin-border bg-admin-surface shadow-soft">
    {children}
  </div>
);

export const TableHead = ({ cols, stickyLastColumn = false }) => (
  <thead>
    <tr className="border-b border-admin-border bg-admin-muted/60">
      {cols.map((col, i) => {
        const isLast = i === cols.length - 1;
        return (
          <th
            key={i}
            className={cx(
              "px-5 py-3 text-[11px] font-semibold uppercase tracking-wider text-admin-textMuted",
              isLast
                ? cx("text-right", stickyLastColumn && "sticky right-0 z-20 border-l border-admin-border bg-admin-muted shadow-[-6px_0_14px_-6px_rgba(15,23,42,0.08)]")
                : "text-left"
            )}
          >
            {col}
          </th>
        );
      })}
    </tr>
  </thead>
);

// ─── StatusBadge ─────────────────────────────────────────────────────────────
export const StatusBadge = ({ active, labelOn = "Hiện", labelOff = "Ẩn" }) => (
  <Badge tone={active ? "success" : "neutral"} dot>
    {active ? labelOn : labelOff}
  </Badge>
);

// ─── Action buttons ───────────────────────────────────────────────────────────
// variant="ghost" (default): nhẹ, bảng chung. variant="solid": nền màu, dễ nhìn (vd AdminUsers).
export const EditBtn = ({ onClick, variant = "ghost" }) => {
  const solid =
    "flex h-9 w-9 items-center justify-center rounded-lg bg-admin-primary text-white shadow-sm transition hover:bg-admin-primaryHover active:scale-[0.97]";
  const ghost =
    "flex h-8 w-8 items-center justify-center rounded-lg text-admin-textMuted transition hover:bg-admin-muted hover:text-admin-text";
  return (
    <button type="button" onClick={onClick} title="Sửa" aria-label="Sửa" className={variant === "solid" ? solid : ghost}>
      <Pencil size={variant === "solid" ? 15 : 14} />
    </button>
  );
};

export const DeleteBtn = ({ onClick, variant = "ghost" }) => {
  const solid =
    "flex h-9 w-9 items-center justify-center rounded-lg bg-rose-600 text-white shadow-sm transition hover:bg-rose-700 active:scale-[0.97]";
  const ghost =
    "flex h-8 w-8 items-center justify-center rounded-lg text-admin-textMuted transition hover:bg-rose-50 hover:text-rose-600";
  return (
    <button type="button" onClick={onClick} title="Xoá" aria-label="Xoá" className={variant === "solid" ? solid : ghost}>
      <Trash2 size={variant === "solid" ? 15 : 14} />
    </button>
  );
};

// ─── SearchBar ────────────────────────────────────────────────────────────────
export const SearchBar = ({ value, onChange, placeholder = "Tìm kiếm...", className }) => (
  <div
    className={cx(
      "flex h-10 items-center gap-2 rounded-lg border border-admin-border bg-admin-surface px-3 transition focus-within:border-admin-primary focus-within:ring-4 focus-within:ring-admin-primary/10",
      className
    )}
  >
    <Search size={15} className="shrink-0 text-admin-textMuted" />
    <input
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      aria-label={placeholder}
      className="min-w-0 flex-1 bg-transparent text-sm text-admin-text outline-none placeholder:text-admin-textMuted/60 sm:w-48"
    />
    {value ? (
      <button
        type="button"
        aria-label="Xoá tìm kiếm"
        onClick={() => onChange({ target: { value: "" } })}
        className="flex h-5 w-5 items-center justify-center rounded-full text-admin-textMuted hover:bg-admin-muted hover:text-admin-text"
      >
        <X size={12} />
      </button>
    ) : null}
  </div>
);

// ─── PrimaryBtn ───────────────────────────────────────────────────────────────
export const PrimaryBtn = ({ onClick, children, icon, disabled = false, ...rest }) => (
  <Button variant="primary" onClick={onClick} disabled={disabled} icon={icon} {...rest}>
    {children}
  </Button>
);

// ─── CascadingDropdown ────────────────────────────────────────────────────────
export const CascadingDropdown = ({
  label,
  value,
  onChange,
  options = [],
  placeholder = "Chọn...",
  disabled = false,
  className = "",
}) => (
  <div className={cx("space-y-1.5", className)}>
    {label && <label className="text-xs font-semibold text-admin-text">{label}</label>}
    <select
      value={value}
      onChange={onChange}
      disabled={disabled}
      className={cx(
        "h-10 w-full rounded-lg border border-admin-border bg-admin-surface px-3 text-sm text-admin-text transition hover:border-admin-textMuted/40 focus:border-admin-primary focus:outline-none focus:ring-4 focus:ring-admin-primary/10 disabled:bg-admin-muted disabled:text-admin-textMuted",
        disabled ? "cursor-not-allowed" : "cursor-pointer"
      )}
    >
      <option value="">{placeholder}</option>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  </div>
);

// ─── CascadingFilters ─────────────────────────────────────────────────────────
export const CascadingFilters = ({ categories = [], selectedCategory, onCategoryChange, className = "" }) => (
  <div className={cx("flex flex-wrap items-center gap-2", className)}>
    <CascadingDropdown
      label=""
      value={selectedCategory}
      onChange={onCategoryChange}
      options={categories.map((cat) => ({ value: cat._id, label: cat.name }))}
      placeholder="Tất cả danh mục"
      className="min-w-[160px]"
    />
  </div>
);

// ─── EmptyState ───────────────────────────────────────────────────────────────
export const EmptyState = ({ icon: Icon = Inbox, title, description, action }) => (
  <div className="flex min-h-[14rem] flex-col items-center justify-center gap-3 px-6 py-10 text-center">
    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-admin-muted text-admin-textMuted">
      <Icon size={22} strokeWidth={1.6} />
    </span>
    <div>
      <p className="text-sm font-semibold text-admin-text">{title}</p>
      {description && <p className="mx-auto mt-1 max-w-sm text-xs leading-relaxed text-admin-textMuted">{description}</p>}
    </div>
    {action ? <div className="mt-1">{action}</div> : null}
  </div>
);

// ─── Skeleton ─────────────────────────────────────────────────────────────────
export const Skeleton = ({ className }) => (
  <div className={cx("animate-pulse rounded-md bg-admin-muted", className)} />
);

export const TableSkeleton = ({ cols = 5, rows = 6 }) => (
  <div className="divide-y divide-admin-border">
    {Array.from({ length: rows }).map((_, r) => (
      <div key={r} className="flex items-center gap-4 px-5 py-4">
        <Skeleton className="h-9 w-9 shrink-0 rounded-lg" />
        {Array.from({ length: cols - 1 }).map((__, c) => (
          <div key={c} className="flex-1">
            <Skeleton className={cx("h-3.5 rounded-full", c === 0 ? "w-32" : c === cols - 2 ? "w-16" : "w-24")} />
          </div>
        ))}
      </div>
    ))}
  </div>
);

// ─── Spinner (full-height centered) ──────────────────────────────────────────
export const Spinner = () => (
  <div className="flex h-52 items-center justify-center" role="status">
    <div className="flex flex-col items-center gap-3">
      <div className="h-8 w-8 animate-spin rounded-full border-[3px] border-admin-muted border-t-admin-primary" />
      <p className="text-xs font-medium text-admin-textMuted">Đang tải...</p>
    </div>
  </div>
);

// ─── PageLoader (overlay for full-page) ──────────────────────────────────────
export const PageLoader = ({ show, message = "Đang xử lý..." }) => {
  if (!show) return null;
  return (
    <AdminPortal>
      <div className="fixed inset-0 z-[500] flex items-center justify-center bg-slate-900/30 backdrop-blur-[2px]" role="status">
        <div className="flex items-center gap-3 rounded-xl border border-admin-border bg-admin-surface px-5 py-4 shadow-soft-xl">
          <Loader2 size={20} className="animate-spin text-admin-primary" />
          <p className="text-sm font-medium text-admin-text">{message}</p>
        </div>
      </div>
    </AdminPortal>
  );
};
